import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    PlanType,
    createPlanType,
    deletePlanType,
    listPlanTypes,
    updatePlanType,
} from '../../api/plantype';
import { ProcessArch, listProcessArchs } from '../../api/dict';
import { Device, listAllDevices } from '../../api/device';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import DeviceKeysPicker from '../../components/DeviceKeysPicker';
import DeviceListModal from '../../components/DeviceListModal';
import Modal from '../../components/Modal';
import { alertDialog, confirmDialog } from '../../utils/dialog';
import { invalidatePlanTypes } from '../../utils/usePlanTypes';
import { useCan } from '../../store';

// 方案类型管理 (2026-10-30 独立表 plan_type): 参数 params.planType 的取值来源.
// 按工艺架构分别维护, 每条可绑定到具体设备: 不绑 = 该架构内全部设备可选, 绑了只在这些设备的参数上可选.
// 写操作与其它基础数据一致: 管理员立即生效 / 普通用户提交后走审批工单. 权限归「参数」域 (param_row).
function PlanTypeManage() {
    const canCreate = useCan('param_row', 'create');
    const canUpdate = useCan('param_row', 'update');
    const canDelete = useCan('param_row', 'delete');
    const canManage = canUpdate || canDelete;

    // 只列启用的工艺架构 (与参数管理顶部 chip 同口径)
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [arch, setArch] = useState('');
    useEffect(() => {
        listProcessArchs()
            .then((l) => {
                setArchs(l);
                setArch((cur) =>
                    l.some((a) => a.code === cur) ? cur : l[0]?.code || '',
                );
            })
            .catch(() => setArchs([]));
    }, []);
    const archLabel = archs.find((a) => a.code === arch)?.label || arch;

    const [list, setList] = useState<PlanType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        if (!arch) return;
        setLoading(true);
        setError('');
        try {
            setList(
                await listPlanTypes({ archCode: arch, includeDisabled: true }),
            );
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [arch]);
    useEffect(() => {
        load();
    }, [load]);

    // 当前架构下的设备: 绑定设备多选的候选 + 「N 台」弹窗里的设备详情
    const [devices, setDevices] = useState<Device[]>([]);
    useEffect(() => {
        if (!arch) return undefined;
        let stale = false;
        listAllDevices({ archCode: arch })
            .then((ds) => !stale && setDevices(ds))
            .catch(() => !stale && setDevices([]));
        return () => {
            stale = true;
        };
    }, [arch]);

    // 新增 / 编辑弹窗 (editing 非空 = 编辑态, code 锁定: 它已写进参数行, 改名会让存量行变成「不在字典」)
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<PlanType | null>(null);
    const [fCode, setFCode] = useState('');
    const [fLabel, setFLabel] = useState('');
    const [fSort, setFSort] = useState(0);
    const [fEnabled, setFEnabled] = useState(true);
    const [fDevices, setFDevices] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const openCreate = () => {
        setEditing(null);
        setFCode('');
        setFLabel('');
        setFSort(list.length + 1);
        setFEnabled(true);
        setFDevices([]);
        setShowForm(true);
    };
    const openEdit = (p: PlanType) => {
        setEditing(p);
        setFCode(p.code);
        setFLabel(p.label);
        setFSort(p.sort);
        setFEnabled(p.isEnabled);
        setFDevices(p.deviceKeys || []);
        setShowForm(true);
    };
    const onSave = async () => {
        if (!fCode.trim() || !fLabel.trim()) {
            alertDialog('编码 / 名称 不能为空');
            return;
        }
        setBusy(true);
        try {
            const res = editing
                ? await updatePlanType(arch, editing.code, {
                      label: fLabel.trim(),
                      sort: fSort,
                      isEnabled: fEnabled,
                      deviceKeys: fDevices,
                  })
                : await createPlanType({
                      archCode: arch,
                      code: fCode.trim(),
                      label: fLabel.trim(),
                      sort: fSort,
                      isEnabled: fEnabled,
                      deviceKeys: fDevices,
                  });
            setShowForm(false);
            invalidatePlanTypes();
            await load(); // 走审批流时数据未变, load 只是回读原列表
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(
                `${editing ? '更新' : '新增'}失败: ` +
                    (e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e)),
            );
        } finally {
            setBusy(false);
        }
    };
    const onToggle = async (p: PlanType) => {
        try {
            const res = await updatePlanType(p.archCode, p.code, {
                isEnabled: !p.isEnabled,
            });
            invalidatePlanTypes();
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(
                '切换状态失败: ' +
                    (e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e)),
            );
        }
    };
    const onDelete = async (p: PlanType) => {
        if (
            !(await confirmDialog(
                `确认删除方案类型「${p.label}」(${p.code})? 已用该值保存的参数行不会被改动, 但会显示为「不在字典」。`,
            ))
        ) {
            return;
        }
        try {
            const res = await deletePlanType(p.archCode, p.code);
            invalidatePlanTypes();
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(
                '删除失败: ' +
                    (e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e)),
            );
        }
    };

    // 「N 台」点开的设备清单 (按绑定顺序; 当前架构设备列表里找不到的回退成只有 deviceKey 的行)
    const [devicesOf, setDevicesOf] = useState<PlanType | null>(null);
    const devicesOfList = useMemo(
        () =>
            (devicesOf?.deviceKeys || []).map((k) => {
                const d = devices.find((x) => x.deviceKey === k);
                return d
                    ? {
                          deviceKey: k,
                          name: d.name,
                          family: d.family,
                          archCode: d.archCode,
                          stdW: d.capability?.stdW,
                      }
                    : { deviceKey: k };
            }),
        [devicesOf, devices],
    );

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">方案类型</div>
                    <div className="text-xs text-slate-500">
                        参数「方案类型」字段的可选值, 按工艺架构分别维护,
                        可绑定到具体设备; 管理员操作立即生效,
                        普通用户提交后走审批工单。
                    </div>
                </div>
                <button
                    type="button"
                    className="btn-primary"
                    onClick={openCreate}
                    hidden={!canCreate || !arch}
                >
                    + 新增
                </button>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-slate-400 mr-1">工艺架构:</span>
                {archs.map((a) => (
                    <span
                        key={a.code}
                        className={`tag cursor-pointer ${
                            a.code === arch
                                ? 'bg-brand-500 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        onClick={() => setArch(a.code)}
                    >
                        {a.icon} {a.label}
                    </span>
                ))}
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error}
                </div>
            )}

            <div className="card p-4">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-16">排序</th>
                            <th className="th">编码</th>
                            <th className="th">名称</th>
                            <th className="th">适用设备</th>
                            <th className="th w-24" hidden={!canUpdate}>
                                状态
                            </th>
                            <th className="th w-32" hidden={!canManage}>
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={6}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={6}
                                >
                                    {archLabel} 还没配置方案类型
                                    {canCreate ? ', 点「+ 新增」添加' : ''}
                                </td>
                            </tr>
                        )}
                        {list.map((p) => {
                            // 禁用行只淡化信息列, 状态与操作列保持全亮 (禁用项恰恰最需要点「启用/编辑/删除」)
                            const dim = p.isEnabled ? '' : 'opacity-50';
                            return (
                                <tr key={p.code} className="hover:bg-slate-50">
                                    <td
                                        className={`td font-mono text-xs ${dim}`}
                                    >
                                        {p.sort}
                                    </td>
                                    <td
                                        className={`td font-mono text-xs ${dim}`}
                                    >
                                        {p.code}
                                    </td>
                                    <td className={`td ${dim}`}>{p.label}</td>
                                    <td className={`td text-xs ${dim}`}>
                                        {p.deviceKeys?.length ? (
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="查看绑定的设备"
                                                onClick={() => setDevicesOf(p)}
                                            >
                                                {p.deviceKeys.length} 台 →
                                            </button>
                                        ) : (
                                            <span className="text-slate-400">
                                                {archLabel} 全部设备
                                            </span>
                                        )}
                                    </td>
                                    <td className="td" hidden={!canUpdate}>
                                        <button
                                            type="button"
                                            onClick={() => onToggle(p)}
                                            className={`text-xs px-2 py-0.5 rounded ${
                                                p.isEnabled
                                                    ? 'bg-emerald-50 text-emerald-700'
                                                    : 'bg-slate-100 text-slate-500'
                                            }`}
                                        >
                                            {p.isEnabled ? '启用' : '停用'}
                                        </button>
                                    </td>
                                    <td
                                        className="td whitespace-nowrap"
                                        hidden={!canManage}
                                    >
                                        <button
                                            type="button"
                                            className="text-brand-600 hover:underline text-xs mr-3"
                                            onClick={() => openEdit(p)}
                                            hidden={!canUpdate}
                                        >
                                            编辑
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            onClick={() => onDelete(p)}
                                            hidden={!canDelete}
                                        >
                                            删除
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="text-[11px] text-slate-400">
                「适用设备」不绑 = 该架构内全部设备的参数都能选到;
                绑定后只有所选设备 (参数里的「型号」) 能选到。 编码已写进参数行
                (params.planType), 建好后不可改。
            </div>

            <DeviceListModal
                open={!!devicesOf}
                title={`适用「${devicesOf?.label || ''}」的设备 · ${devicesOfList.length} 台`}
                hint={`方案类型绑定的设备 (仅这些设备的参数能选到它), 绑定在「数据字典 · 方案类型」里维护。工艺架构 ${archLabel}`}
                devices={devicesOfList}
                tag={devicesOf?.label}
                onClose={() => setDevicesOf(null)}
            />

            <Modal
                open={showForm}
                title={
                    editing
                        ? `编辑方案类型 · ${editing.code}`
                        : `新增方案类型 · ${archLabel}`
                }
                width={560}
                onClose={() => setShowForm(false)}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                编码 (建后不可改)
                            </label>
                            <input
                                className="inp w-full font-mono"
                                placeholder="例: standard / blue"
                                value={fCode}
                                disabled={!!editing}
                                onChange={(e) => setFCode(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                名称
                            </label>
                            <input
                                className="inp w-full"
                                placeholder="例: 标准压力 / 蓝光推荐参数"
                                value={fLabel}
                                onChange={(e) => setFLabel(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                排序 (越小越靠前)
                            </label>
                            <input
                                className="inp w-full"
                                type="number"
                                value={fSort}
                                onChange={(e) =>
                                    setFSort(Number(e.target.value || 0))
                                }
                            />
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm self-end pb-2">
                            <input
                                type="checkbox"
                                checked={fEnabled}
                                onChange={(e) => setFEnabled(e.target.checked)}
                            />
                            启用 (停用项不在参数表单里展示)
                        </label>
                    </div>
                    <DeviceKeysPicker
                        title="绑定设备 (不勾 = 该架构全部设备, 勾选后只有这些设备的参数能选到)"
                        items={devices.map((d) => ({
                            code: d.deviceKey,
                            name: d.name || d.deviceKey,
                        }))}
                        value={fDevices}
                        onChange={setFDevices}
                        emptyHint="该工艺架构暂无设备"
                        allHint={`当前对 ${archLabel} 全部设备生效`}
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn-ghost"
                            disabled={busy}
                            onClick={() => setShowForm(false)}
                        >
                            取消
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={busy}
                            onClick={onSave}
                        >
                            {busy ? '保存中…' : '保存'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default PlanTypeManage;
